import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GiftProduct, GiftProductType, GiftProductHelper, IntegrationType } from '../../../../models';
import VirtualProductForm from '../forms/VirtualProductForm';
import CashbackProductForm from '../forms/CashbackProductForm';
import ExistingProductForm from '../forms/ExistingProductForm';
import FuelDiscountProductForm from '../forms/FuelDiscountProductForm';
import PredictionTicketProductForm from '../forms/PredictionTicketProductForm';
import DrawingNumberProductForm from '../forms/DrawingNumberProductForm';
import SpinTicketProductForm from '../forms/SpinTicketProductForm';

interface ProductEditorModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (product: GiftProduct) => void;
  productType: GiftProductType | null;
  // Recompensa em dinheiro: produto virtual com valor de face, cadastrado por outro
  // formulário (só o valor). Na edição o próprio cashValue já identifica.
  cashbackMode?: boolean;
  initialProduct?: GiftProduct | null;
  fuels: any[];
}

const ProductEditorModal: React.FC<ProductEditorModalProps> = ({
  open,
  onClose,
  onSave,
  productType,
  cashbackMode = false,
  initialProduct,
  fuels,
}) => {
  const [product, setProduct] = useState<Partial<GiftProduct>>({});

  useEffect(() => {
    if (open && productType) {
      if (initialProduct) {
        setProduct(initialProduct);
      } else {
        setProduct({ type: productType });
      }
    }
  }, [open, productType, initialProduct]);

  const isCashback = (initialProduct?.cashValue ?? null) !== null || cashbackMode;

  const handleSave = () => {
    if (isValidProduct()) {
      const fullProduct = GiftProductHelper.createGiftProduct(product);
      onSave(fullProduct);
      handleClose();
    }
  };

  const handleClose = () => {
    setProduct({});
    onClose();
  };

  const isValidProduct = (): boolean => {
    if (!product.type) return false;

    switch (product.type) {
      case GiftProductType.Virtual:
        if (isCashback) { return (product.cashValue ?? 0) > 0 && !!product.productName; }
        return !!product.productName;
      case GiftProductType.Existing:
        return !!product.productId;
      case GiftProductType.FuelDiscount:
        // Must have a linked Discount
        return !!product.discountId;
      case GiftProductType.PredictionTicket:
        return !!product.bolaoId;
      case GiftProductType.DrawingNumber:
        return !!product.drawingId && (product.luckyNumberAmount ?? 0) >= 1;
      case GiftProductType.SpinTicket:
        return !!product.roletaId && (product.spinAmount ?? 0) >= 1;
      default:
        return false;
    }
  };

  const getTitle = (): string => {
    if (initialProduct) {
      return isCashback ? 'Editar Cashback' : 'Editar Produto';
    }
    switch (productType) {
      case GiftProductType.Virtual:
        return isCashback ? 'Adicionar Cashback (Vale em Dinheiro)' : 'Adicionar Produto Virtual';
      case GiftProductType.Existing:
        return 'Adicionar Produto Existente';
      case GiftProductType.FuelDiscount:
        return 'Adicionar Desconto no Abastecimento';
      case GiftProductType.PredictionTicket:
        return 'Adicionar Direito ao Palpite (Bolão)';
      case GiftProductType.DrawingNumber:
        return 'Adicionar Número da Sorte (Sorteio)';
      case GiftProductType.SpinTicket:
        return 'Adicionar Giro na Roleta';
      default:
        return 'Adicionar Produto';
    }
  };

  const renderForm = () => {
    if (!productType) return null;

    switch (productType) {
      case GiftProductType.Virtual:
        return isCashback
          ? <CashbackProductForm product={product} onChange={setProduct} />
          : <VirtualProductForm product={product} onChange={setProduct} />;
      case GiftProductType.Existing:
        return <ExistingProductForm product={product} onChange={setProduct} />;
      case GiftProductType.FuelDiscount:
        return <FuelDiscountProductForm product={product} onChange={setProduct} fuels={fuels} />;
      case GiftProductType.PredictionTicket:
        return <PredictionTicketProductForm product={product} onChange={setProduct} />;
      case GiftProductType.DrawingNumber:
        return <DrawingNumberProductForm product={product} onChange={setProduct} />;
      case GiftProductType.SpinTicket:
        return <SpinTicketProductForm product={product} onChange={setProduct} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {getTitle()}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={1}>
          {renderForm()}
          {!isCashback
            && productType !== GiftProductType.FuelDiscount
            && productType !== GiftProductType.PredictionTicket
            && productType !== GiftProductType.DrawingNumber
            && productType !== GiftProductType.SpinTicket && (
            <Box mt={3}>
              <TextField
                select
                fullWidth
                label="Tipo de Integração"
                value={product.integrationType || ''}
                onChange={(e) => setProduct({
                  ...product,
                  integrationType: e.target.value ? e.target.value as IntegrationType : null
                })}
                helperText="Selecione o tipo de integração para este produto (opcional)"
              >
                <MenuItem value="">Nenhum</MenuItem>
                <MenuItem value={IntegrationType.rti}>RTI</MenuItem>
                <MenuItem value={IntegrationType.evo}>EVO</MenuItem>
                <MenuItem value={IntegrationType.teixeiraPinto}>Teixeira Pinto</MenuItem>
                <MenuItem value={IntegrationType.trevo}>Top Banho (Rede Trevo)</MenuItem>
              </TextField>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValidProduct()}
        >
          {initialProduct ? 'Salvar' : 'Adicionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductEditorModal;
